import firebase from 'firebase';
import _ from 'lodash';
import { map, init, isNil } from 'lodash/fp';
import { getVal, keyAsId, isNotNil } from '../utils';

const mapObjects = map.convert({ cap: false });
// Setup
// --------------
 const _Firechat = function() {
    // Establish a reference to the `window` object, and save the previous value
    // of the `Firechat` variable.
    // var root = this,
    //     previousFirechat = root.Firechat;

    let instance = null;

    function Firechat(firebaseRef, options) {

      // Cache the provided Database reference and the firebase.App instance
      this._firechatRef = firebaseRef;
      this._firebaseApp = firebaseRef.database.app;

      // User-specific instance variables.
      this._user = null;
      this._userId = null;
      this._userName = null;
      this._isModerator = false;
      this._currentRoom = null;

      // A unique id generated for each session.
      this._sessionId = null;

      // A mapping of event IDs to an array of callbacks.
      this._events = {};

      // A mapping of room IDs to a boolean indicating presence.
      this._rooms = {};

      // A mapping of operations to re-queue on disconnect.
      this._presenceBits = {};

      // Commonly-used Firebase references.
      this._userRef        = null;
      this._messageRef     = this._firechatRef.child('room-messages');
      this._roomRef        = this._firechatRef.child('room-metadata');
      this._privateRoomRef = this._firechatRef.child('room-private-metadata');
      this._moderatorsRef  = this._firechatRef.child('moderators');
      this._suspensionsRef = this._firechatRef.child('suspensions');
      this._usersOnlineRef = this._firechatRef.child('user-names-online');

      // Setup and establish default options.
      this._options = options || {};

      // The number of historical messages to load per room.
      this._options.numMaxMessages = this._options.numMaxMessages || 25;
    }

    // Run Firechat in *noConflict* mode, returning the `Firechat` variable to
    // its previous owner, and returning a reference to the Firechat object.
    // Firechat.noConflict = function noConflict() {
    //   root.Firechat = previousFirechat;
    //   return Firechat;
    // };

    // Export the Firechat object as a global.
    //root.Firechat = Firechat;

    // Firechat Internal Methods
    // --------------
    Firechat.prototype = {

      // Load the initial metadata for the user's account and set initial state.
      _loadUserMetadata: function(onComplete) {
        var self = this;

        // Update the user record with a default name on user's first visit.
        this._userRef.transaction(function(current) {
          if (!current || !current.id || !current.name) {
            return {
              id: self._userId,
              name: self._userName,
              via: self._options.via || 'system'
            };
          }
        }, function(error, committed, snapshot) {
          self._user = snapshot.val();
          self._moderatorsRef.child(self._userId).once('value', function(snapshot) {
            self._isModerator = !!snapshot.val();
            if(self._options.via) return;
            setTimeout(onComplete, 0);
          });
          if(self._options.via === 'app' ){
                self.getMyRoomList(room=>{
                    if(room){
                        this.setCurrentRoom(Object.keys(room)[0]);
                        setTimeout(onComplete, 0);
                    }else{
                        self.createRoom(self._userId, null, self._userName)
                        .then(function(room){
                            this.setCurrentRoom(room.id);
                            setTimeout(onComplete, 0);
                        });
                    }

                });
          }
        });
      },

      // Initialize Firebase listeners and callbacks for the supported bindings.
      _setupDataEvents: function() {
        // Monitor connection state so we can requeue disconnect operations if need be.
        var connectedRef = this._firechatRef.root.child('.info/connected');
        connectedRef.on('value', function(snapshot) {
          if (snapshot.val() === true) {
            // We're connected (or reconnected)! Set up our presence state.
            for (var path in this._presenceBits) {
              var op = this._presenceBits[path],
                  ref = op.ref;

              ref.onDisconnect().set(op.offlineValue);
              ref.set(op.onlineValue);
            }
          }
        }, this);

        // Queue up a presence operation to remove the session when presence is lost
        this._queuePresenceOperation(this._sessionRef, true, null);

        // Register our username in the public user listing.
        var usernameRef = this._usersOnlineRef.child(this._userName.toLowerCase());
        var usernameSessionRef = usernameRef.child(this._sessionId);
        this._queuePresenceOperation(usernameSessionRef, {
          id: this._userId,
          name: this._userName
        }, null);

        // Listen for state changes for the given user.
        this._userRef.on('value', this._onUpdateUser, this);

        // Listen for chat invitations from other users.
        this._userRef.child('invites').on('child_added', this._onFirechatInvite, this);

        // Listen for messages from moderators and adminstrators.
        this._userRef.child('notifications').on('child_added', this._onNotification, this);
      },

      // Append the new callback to our list of event handlers.
      _addEventCallback: function(eventId, callback) {
        this._events[eventId] = this._events[eventId] || [];
        this._events[eventId].push(callback);
      },

      // remove callback from our list of event handlers.
      _removeEventCallback: function(eventId, callback) {
        if(this._events[eventId]){
          this._events[eventId].splice(this._events[eventId].indexOf(eventId), 1);
        }

      },

      // Retrieve the list of event handlers for a given event id.
      _getEventCallbacks: function(eventId) {
        if (this._events.hasOwnProperty(eventId)) {
          return this._events[eventId];
        }
        return [];
      },

      // Invoke each of the event handlers for a given event id with specified data.
      _invokeEventCallbacks: function(eventId) {
        var args = [],
            callbacks = this._getEventCallbacks(eventId);

        Array.prototype.push.apply(args, arguments);
        args = args.slice(1);

        for (var i = 0; i < callbacks.length; i += 1) {
          callbacks[i].apply(null, args);
        }
      },

      // Keep track of on-disconnect events so they can be requeued if we disconnect the reconnect.
      _queuePresenceOperation: function(ref, onlineValue, offlineValue) {
        ref.onDisconnect().set(offlineValue);
        ref.set(onlineValue);
        this._presenceBits[ref.toString()] = {
          ref: ref,
          onlineValue: onlineValue,
          offlineValue: offlineValue
        };
      },

      // Remove an on-disconnect event from firing upon future disconnect and reconnect.
      _removePresenceOperation: function(ref, value) {
        var path = ref.toString();
        ref.onDisconnect().cancel();
        ref.set(value);
        delete this._presenceBits[path];
      },

      // Event to monitor current user state.
      _onUpdateUser: function(snapshot) {
        this._user = snapshot.val();
        this._userName = this._user.name;
        this._invokeEventCallbacks('user-update', this._user);
      },

      // Event to monitor current auth + user state.
      _onAuthRequired: function() {
        this._invokeEventCallbacks('auth-required');
      },

      // Events to monitor room entry / exit and messages additional / removal.
      _onEnterRoom: function(room) {
        this.setCurrentRoom(room.id);
        this._invokeEventCallbacks('room-enter', room);
      },
      _onNewMessage: function(roomId, snapshot, ignoreCallback) {
        var message = snapshot.val();
        message.id = snapshot.key;
        this._invokeEventCallbacks('message-add', roomId, message, ignoreCallback);
      },
      _onRemoveMessage: function(roomId, snapshot) {
        var messageId = snapshot.key;
        this._invokeEventCallbacks('message-remove', roomId, messageId);
      },
      _onLeaveRoom: function(roomId) {
        this._invokeEventCallbacks('room-exit', roomId);
      },

      // Event to listen for notifications from administrators and moderators.
      _onNotification: function(snapshot) {
        var notification = snapshot.val();
        if (!notification.read) {
          if (notification.notificationType !== 'suspension' || notification.data.suspendedUntil < new Date().getTime()) {
            snapshot.ref.child('read').set(true);
          }
          this._invokeEventCallbacks('notification', notification);
        }
      },

      // Events to monitor chat invitations and invitation replies.
      _onFirechatInvite: function(snapshot) {
        var self = this,
            invite = snapshot.val();

        // Skip invites we've already responded to.
        if (invite.status) {
          return;
        }

        invite.id = invite.id || snapshot.key;
        self.getRoom(invite.roomId).then(function(room) {
          invite.toRoomName = room.name;
          self._invokeEventCallbacks('room-invite', invite);
        });
      },
      _onFirechatInviteResponse: function(snapshot) {
        var self = this,
            invite = snapshot.val();

        invite.id = invite.id || snapshot.key;
        this._invokeEventCallbacks('room-invite-response', invite);
      }
    };

    function getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    }

      // Firechat External Methods
      // --------------

      // Initialize the library and setup data listeners.
      Firechat.prototype.setUser = function (userId, userName, callback) {
        var self = this;

        let token = getCookie('userToken');

        firebase.auth()
          .signInWithCustomToken(token)
          .then((userCredentials) => {

            self._userId = userId.toString();
            self._userName = userName.toString();
            self._userRef = self._firechatRef.child('users').child(self._userId);
            self._sessionRef = self._userRef.child('sessions').push();
            self._sessionId = self._sessionRef.key;

            self._loadUserMetadata(function () {
              setTimeout(function () {
                callback && callback(self._user);
                self._setupDataEvents();
              }, 0);
            });
          }).catch((error) => {
            console.log(error);
          });
      };

      Firechat.prototype.setCurrentRoom = function(roomId) {
        this._currentRoom = roomId;
      }

      Firechat.prototype.setChatFocused = function(flag=false) {
        this._userRef.update({
          chat_is_focused: flag
        });
      }

    // Resumes the previous session by automatically entering rooms.
    Firechat.prototype.resumeSession = function() {
      this._userRef.child('rooms').once('value', function(snapshot) {
        var rooms = snapshot.val();
        for (var roomId in rooms) {
          this.enterRoom(rooms[roomId].id);
        }
      }, /* onError */ function(){}, /* context */ this);
    };

    // Callback registration. Supports each of the following events:
    Firechat.prototype.on = function(eventType, cb) {
      this._addEventCallback(eventType, cb);
    };
    Firechat.prototype.off = function(eventType, cb) {
      this._removeEventCallback(eventType, cb);
    };

    Firechat.prototype.checkIfRoomExists = function(userId){
      return new Promise((resolve,reject) => {
          this._roomRef.orderByChild('name').equalTo(userId).once('value')
        .then(getVal)
        .then(val => {
          const exists = val ? Object.values(val)[0] : null;
          return exists;
        })
        .then(resolve)
        .catch(error => {
          console.error(error);
          reject(error);
        });
      });
    }

    Firechat.prototype.checkIfChatExists = function(chatId, cb){
      return new Promise((resolve,reject) => {
        this._userRef.child('chats').orderByKey().equalTo(chatId).once('value')
          .then(getVal)
          .then(chat=>{
          const chatKey = chat ? Object.keys(chat)[0] : null;
          return chatKey
        })
        .then(resolve)
        .catch(error => {
          console.error(error);
          reject(error);
        });
      });
    }
    // Create and automatically enter a new chat room.
    Firechat.prototype.createRoom = function(roomName, roomType, userName) {
        let self = this;
        let newRoomRef = this._roomRef.push();


        let newRoom = {
          id: newRoomRef.key,
          name: roomName,
          type: roomType || 'private',
          createdByUserId: this._userId,
          createdAt: firebase.database.ServerValue.TIMESTAMP,
          userName,
        };

        if (roomType === 'private') {
          newRoom.authorizedUsers = {};
          newRoom.authorizedUsers[this._userId] = true;
        }

        return new Promise((resolve, reject) => {
          newRoomRef.set(newRoom)
          .then(() => self.enterRoom(newRoomRef.key))
          .then(resolve)
          .catch(function(error) {
            console.error(error);
            reject(error);
          });
        });

    };

    Firechat.prototype.joinUserAppToRoom = function(userId,userName, roomId){
      return new Promise(resolve => {
        const userRef = this._firechatRef.child('users').child(userId);
          userRef.once('value')
            .then(snapshot => {
              const userExists = snapshot.val();
              if(userExists){
                userRef.child('rooms').child(roomId).set({
                  id: roomId,
                  name: userId,
                  active: true
                });
              } else {
                userRef.set({
                  id : userId,
                  name : userName,
                  via : 'app',
                    rooms : {
                      [roomId] : {
                        id : roomId,
                        name : userId,
                        active : true
                    }
                  }
                });
              }
            })
            .then(resolve);
      });
    }

    // Enter a chat room.
    Firechat.prototype.enterRoom = function(roomId, guestName=null, ignoreCallback = false) {
      var self = this;

      return new Promise(resolve => {
        if(self._currentRoom != roomId)
          self.leaveRoom(roomId);

        self.getRoom(roomId).then(function(room) {
          var roomName = room.name;

          if (!roomId || !roomName) return;

          //self.leaveRoom(roomId);

          if(roomId == self._currentRoom){
            return;
          }

          self._rooms[roomId] = true;

          let updateRoomMetaData = {};

          // atualizando o nome do usuário, caso não tenha
          // por questão de compatibilidade com conversas antigas
          if(!room.userName&&guestName)
            updateRoomMetaData["userName"] = guestName;

          self._roomRef.child(roomId).update(updateRoomMetaData);
          //#RoomMetaData END

          if (self._user) {
            // Save entering this room to resume the session again later.
            self._userRef.child('rooms').child(roomId).set({
              id: roomId,
              name: roomName,
              guestName,
              active: true
            });

            // Set presence bit for the room and queue it for removal on disconnect.
            var presenceRef = self._firechatRef.child('room-users').child(roomId).child(self._userId).child(self._sessionId);
            self._queuePresenceOperation(presenceRef, {
              id: self._userId,
              name: self._userName
            }, null);
          }

          // Invoke our callbacks before we start listening for new messages.
          self._onEnterRoom({ id: roomId, name: roomName });
          // Setup message listeners
          self._roomRef.child(roomId).once('value', function(snapshot) {
            self._messageRef.child(roomId).limitToLast(self._options.numMaxMessages).on('child_added', function(snapshot) {
              self._onNewMessage(roomId, snapshot, ignoreCallback);
            }, /* onCancel */ function() {
              // Turns out we don't have permission to access these messages.
              self.leaveRoom(roomId);
            }, /* context */ self);

            self._messageRef.child(roomId).limitToLast(self._options.numMaxMessages).on('child_removed', function(snapshot) {
              self._onRemoveMessage(roomId, snapshot);
            }, /* onCancel */ function(){}, /* context */ self);
          }, /* onFailure */ function(){}, self);
          resolve(room);
        });
      });
    };

    // Leave a chat room.
    Firechat.prototype.leaveRoom = function(roomId) {
      var self = this,

      userRoomRef = self._firechatRef.child('room-users').child(roomId);

      // Remove listener for new messages to this room.
      self._messageRef.child(roomId).off();

      if (self._user) {
        var presenceRef = userRoomRef.child(self._userId).child(self._sessionId);

        // Remove presence bit for the room and cancel on-disconnect removal.
        self._removePresenceOperation(presenceRef, null);

        // Remove session bit for the room.
        self._userRef.child('rooms').child(roomId).remove();
      }

      delete self._rooms[roomId];

      self.setCurrentRoom(null);

      // Invoke event callbacks for the room-exit event.
      self._onLeaveRoom(roomId);

    };

    Firechat.prototype.sendMessage = function(roomId, messageContent, messageType, cb) {

      var self = this,
          message = {
            userId: self._userId,
            name: self._userName,
            timestamp: firebase.database.ServerValue.TIMESTAMP,
            message: messageContent,
            type: messageType || 'system'
          },
          newMessageRef;

        if(!roomId && self._options.via === 'app')
            roomId = self._currentRoom;
        else if(!roomId && self._currentRoom)
            roomId = self._currentRoom;


        if (!self._user) return;
          console.log('No send Message', self._currentRoom)
      newMessageRef = self._messageRef.child(roomId).push();
      newMessageRef.setWithPriority(message, firebase.database.ServerValue.TIMESTAMP, cb);
      self.updateConversation(roomId, message.message);
    };

    Firechat.prototype.deleteMessage = function(roomId, messageId, cb) {
      var self = this;

      self._messageRef.child(roomId).child(messageId).remove(cb);
    };

    Firechat.prototype.sendMessageWidget = function(roomId, messageContent, messageType, widgetType, cb) {

      if(!roomId) return;

      var self = this,
        message = {
          userId: self._userId,
          name: self._userName,
          timestamp: firebase.database.ServerValue.TIMESTAMP,
          message: messageContent,
          type: messageType || 'system',
          isWidget: true,
          widgetType
        },
        changedMessage;

      if (!self._user) return;

      changedMessage = self._messageRef.child(roomId).push();
      changedMessage.setWithPriority(message, firebase.database.ServerValue.TIMESTAMP, cb);
    };

    // Mute or unmute a given user by id. This list will be stored internally and
    // all messages from the muted clients will be filtered client-side after
    // receipt of each new message.
    Firechat.prototype.toggleUserMute = function(userId, cb) {
      var self = this;

      if (!self._user) {
        self._onAuthRequired();
        if (cb) {
          cb(new Error('Not authenticated or user not set!'));
        }
        return;
      }

      self._userRef.child('muted').child(userId).transaction(function(isMuted) {
        return (isMuted) ? null : true;
      }, cb);
    };

    // Send a moderator notification to a specific user.
    Firechat.prototype.sendSuperuserNotification = function(userId, notificationType, data, cb) {
      var self = this,
          userNotificationsRef = self._firechatRef.child('users').child(userId).child('notifications');

      userNotificationsRef.push({
        fromUserId: self._userId,
        timestamp: firebase.database.ServerValue.TIMESTAMP,
        notificationType: notificationType,
        data: data || {}
      }, cb);
    };

    // Warn a user for violating the terms of service or being abusive.
    Firechat.prototype.warnUser = function(userId) {
      var self = this;

      self.sendSuperuserNotification(userId, 'warning');
    };

    // Suspend a user by putting the user into read-only mode for a period.
    Firechat.prototype.suspendUser = function(userId, timeLengthSeconds, cb) {
      var self = this,
          suspendedUntil = new Date().getTime() + 1000*timeLengthSeconds;

      self._suspensionsRef.child(userId).set(suspendedUntil, function(error) {
        if (error && cb) {
          return cb(error);
        } else {
          self.sendSuperuserNotification(userId, 'suspension', {
            suspendedUntil: suspendedUntil
          });
          return cb(null);
        }
      });
    };

    // Invite a user to a specific chat room.
    Firechat.prototype.inviteUser = function(userId, roomId) {
      var self = this,
          sendInvite = function() {
            var inviteRef = self._firechatRef.child('users').child(userId).child('invites').push();
            inviteRef.set({
              id: inviteRef.key,
              fromUserId: self._userId,
              fromUserName: self._userName,
              roomId: roomId
            });

            // Handle listen unauth / failure in case we're kicked.
            inviteRef.on('value', self._onFirechatInviteResponse, function(){}, self);
          };

      if (!self._user) {
        self._onAuthRequired();
        return;
      }

      self.getRoom(roomId).then(function(room) {
        if (room.type === 'private') {
          var authorizedUserRef = self._roomRef.child(roomId).child('authorizedUsers');
          authorizedUserRef.child(userId).set(true, function(error) {
            if (!error) {
              sendInvite();
            }
          });
        } else {
          sendInvite();
        }
      });
    };

    Firechat.prototype.acceptInvite = function(inviteId, cb) {
      var self = this;

      self._userRef.child('invites').child(inviteId).once('value', function(snapshot) {
        var invite = snapshot.val();
        if (invite === null && cb) {
          return cb(new Error('acceptInvite(' + inviteId + '): invalid invite id'));
        } else {
          self.enterRoom(invite.roomId);
          self._userRef.child('invites').child(inviteId).update({
            'status': 'accepted',
            'toUserName': self._userName
          }, cb);
        }
      }, self);
    };

    Firechat.prototype.declineInvite = function(inviteId, cb) {
      var self = this,
          updates = {
            'status': 'declined',
            'toUserName': self._userName
          };

      self._userRef.child('invites').child(inviteId).update(updates, cb);
    };

    Firechat.prototype.getRoomList = function(cb) {
      var self = this;

      self._roomRef.once('value', function(snapshot) {
        cb(snapshot.val());
      });

    };

    Firechat.prototype.getMessagesList = function(roomId, index, cb) {
      const limite = 5;
      const ref = this._messageRef.child(roomId).limitToLast( limite );
      if(index > 1){
        ref.endAt(limite * index)
      }

      ref.once('value',(messages)=>{})
    }


    Firechat.prototype.getOtherRooms = function(cb) {
       this._roomRef.orderByChild('attended').equalTo(false).on('value', function(snapshot){
          cb&&cb(snapshot.val());
       })
    }

    Firechat.prototype.attendUser = function(roomId) {
      const self = this;
      return new Promise((resolve, reject) => {
        this._roomRef
         .child(roomId)
         .update({
           attended: false,
           attendentId: self._userId,
           attendentName: self._userName,
           waiting: false
         })
         .then(() => self.getRoom(roomId))
         .then(resolve)
         .catch(reject);
      });
    }

    //------------- Conversation ----------------------------
    Firechat.prototype.newConversation = function(roomId, guestName="undefined", guestId, lastMessage='', departmentId=null, departmentName=null, resetAttended = false) {

      if(resetAttended)
        this._roomRef.child(roomId).update({attended : false});

      var self = this;

      const _userChatRef = self._userRef.child('chats').child(roomId);

      return self.getRoom(roomId)
          .then(room => {
              if(!room.attendentId) return;
               return self.closeChat(roomId, room.attendentId)
                  .then(() => self.enterRoom(roomId))
          })
          .then(() => self.setCurrentRoom(roomId))
          .then(() => _userChatRef.set({
              guestName,
              guestId,
              lastMessage,
              timestamp : new Date().getTime(),
              departmentId,
              departmentName,
              status: 'open'
          }))
          .then(()=>{
              const guestRef = self._firechatRef.child('users').child(guestId).child('chats').child(roomId);
              return guestRef.update({
                guestName: self._user.name,
                guestId: self._user.id,
                lastMessage,
                timestamp : new Date().getTime(),
                status: 'open'
             });
          })
          .then(() => self.sendMessageWidget(
            roomId,
          `Conversa iniciada com ${self._user.name}`,
          'system', 'new_conversation2'))
          .then(() => self.attendUser(roomId))
          .then(() => self.getRoom(roomId));
    }

    Firechat.prototype.setAsRead = function(chatId){
      this._userRef.child('chats')
          .child(chatId)
          .update({
            read: true
          });
    }

    Firechat.prototype.setConversationStatus = function(roomId, guestId, status){

      var self = this;
      const _userChatRef = self._userRef.child('chats').child(roomId);

        _userChatRef.update({
           status
        }).then(()=>{
          const guestRef = self._firechatRef
                               .child('users')
                               .child(guestId)
                               .child('chats')
                               .child(roomId);
          return guestRef.update({
            status
         });
        })
    }

    Firechat.prototype.updateConversation = function(roomId, lastMessage) {

      const timestamp = new Date().getTime();

      this._userRef.child('chats').child(roomId).update({
        lastMessage,
        timestamp
     }, ()=>{
        this._roomRef.child(roomId).update({
          lastMessage,
          timestamp
        })
     });
    }

    // Firechat.prototype.getConversations = function(roomId) {
    //   this._userRef.child('chats').child(roomId).on('value',(snap)=>{
    //     this._invokeEventCallbacks('chat-add', snap.val());
    //   })
    // }
    //------------- End Conversation -------------------------


    Firechat.prototype.getMyRoomList = function(cb) {
      var self = this;
        self._userRef.child('rooms').once('value', function(snapshot){
            cb(snapshot.val());
        })
    //   self._roomRef.orderByChild('createdByUserId').equalTo(userId).once('value', function(snapshot) {
    //     cb(snapshot.val());
    //   });
    //   self._roomRef.once('value', function(snapshot) {
    //     cb(snapshot.val());
    //   });
    };
    Firechat.prototype.getChats = function() {
      var self = this;
        self._userRef.child('chats').on('value', function(snap){
          const _chats =  _.orderBy( _.map(snap.val(), (value, key)=>({...value, key})) , ['timestamp'], 'desc')
          self._invokeEventCallbacks('chat-add', _chats);
        })
    };

    Firechat.prototype.closeChat = function(roomId, guestId) {
      const self = this;

      function sleep(time) {
        return new Promise(resolve => {
          setTimeout(resolve,time)
        }) 
      }

      self.setConversationStatus(roomId, guestId, 'closed');

        return self._roomRef.child(roomId).update({
          attended : true,
          attendentId: null,
          attendentName: null,
        })
        .then(() =>  self.sendMessageWidget(roomId, 'Conversa encerrada', 'system', 'finished_conversation'))
        .then(() =>  sleep(300))
        .then(() =>  self.leaveRoom(roomId));
    };

    Firechat.prototype.checkChatIsClosed = function(roomId, cb){
      if(!roomId) return;
      this._roomRef.child(roomId).once('value',(snap)=>{
        cb&&cb(snap.val().attended)
      });
    }

    Firechat.prototype.getAllChats = function() {
      var self = this;
        self._roomRef.on('value', function(snap){
          let _chats =  _.orderBy( _.map(snap.val(), (value, key)=>({...value, key})) , item => item.timestamp || item.createdAt).reverse();

          // Precisa normalizar o nome dos campos
          _chats = _.map(_chats, val=> {
            return {
              ...val,
              guestId: val.name,
              guestName: val.userName || "Undefined",
              timestamp: val.timestamp || val.createdAt
            }
          })

          self._invokeEventCallbacks('chat-add', _chats);
        })
    };

    Firechat.prototype.getUsersByRoom = function(roomId, cb, limit=10) {
      var self = this,
      query = self._firechatRef.child('room-users').child(roomId);

      query = (limit) ? query.limitToLast(limit) : query;

      query.once('value', function(snapshot) {
        var usernames = snapshot.val() || {},
        usernamesUnique = {};

        for (var username in usernames) {
          for (var session in usernames[username]) {
            // Skip all other sessions for this user as we only need one.
            usernamesUnique[username] = usernames[username][session];
            break;
          }
        }

        setTimeout(function() {
          cb(usernamesUnique);
        }, 0);
      });
    };


    Firechat.prototype.getUsersByPrefix = function(prefix, startAt, endAt, limit, cb) {
      var self = this,
          query = this._usersOnlineRef,
          prefixLower = prefix.toLowerCase();

      if (startAt) {
        query = query.startAt(null, startAt);
      } else if (endAt) {
        query = query.endAt(null, endAt);
      } else {
        query = (prefixLower) ? query.startAt(null, prefixLower) : query.startAt();
      }

      query = (limit) ? query.limitToLast(limit) : query;

      query.once('value', function(snapshot) {
        var usernames = snapshot.val() || {},
            usernamesFiltered = {};

        for (var userNameKey in usernames) {
          var sessions = usernames[userNameKey],
              userName, userId, usernameClean;

          // Grab the user data from the first registered active session.
          for (var sessionId in sessions) {
            userName = sessions[sessionId].name;
            userId = sessions[sessionId].id;

            // Skip all other sessions for this user as we only need one.
            break;
          }

          // Filter out any usernames that don't match our prefix and break.
          if ((prefix.length > 0) && (userName.toLowerCase().indexOf(prefixLower) !== 0))
            continue;

          usernamesFiltered[userName] = {
            name: userName,
            id: userId
          };
        }

        setTimeout(function() {
          cb(usernamesFiltered);
        }, 0);
      });
    };

    // Miscellaneous helper methods.
    Firechat.prototype.getRoom = function(roomId) {
      return new Promise((resolve, reject) => {
        this._roomRef.child(roomId)
          .once('value')
          .then(getVal)
          .then(resolve)
          .catch(error => {
            console.error(error);
            reject(error);
          });
      });
    };

    Firechat.prototype.userIsModerator = function() {
      return this._isModerator;
    };

    Firechat.prototype.warn = function(msg) {
      if (console) {
        msg = 'Firechat Warning: ' + msg;
        if (typeof console.warn === 'function') {
          console.warn(msg);
        } else if (typeof console.log === 'function') {
          console.log(msg);
        }
      }
    };

    /*
    Função criada para contornar o problema dos ids no chat (usuário do sistema e usuário do aplicativo poderiam ter o mesmo id)
    Agora, os usuários do sistema terão o id precedido por 999999
    [1 situação]: Usuario #99999922 loga depois de ja ter usado o chat como usuario #22. => fazer update
    [2 situação]: Usuário loga sem nunca ter usado o chat antes. => não precisa fazer nada
    [3 situação]: Usuario loga após já ter usado o chat e feito o update. => não precisa fazer nada
    [4 situação]: Usuario loga com id diferente de 999999. => não precisa fazer nada
    */
    Firechat.prototype.updateSpecialsUsers = function(iduser, cb) {
      console.log("UPDATE SPECIAL USER START ==>", iduser)
      const userBeforeId = iduser.substr(6)
      const _userBefore = this._firechatRef.child('users').child(userBeforeId);
      _userBefore.once('value', snapshot=>{
        const _user = snapshot.val();

        // se não for usuario criado via sistema ou ja tiver sido atualizado não faz nada
        if( !_user || _user.via !== "system" || _user.updated === true) {
          console.log("NOT UPDATE")
          return cb();
        }

        // atuaiza o usuário, apenas sobrescrevendo o id
        this._userRef.update({..._user, id: iduser});
        // setar flag pra saber que já fez o update
        _userBefore.update({updated: true})

        console.log("UPDATE SUCCESSFUL")
        return cb();

      });

    }

    Firechat.prototype.updateUser = function(field=null, value=null, callback) {

      if( !field || !value ) return false;

      this._userRef.update({
        [field]: value
      }, error => {
        if (error) {
          return callback&&callback(error)
        } else {
          return callback&&callback()
        }
      });
    }

    Firechat.prototype.changeDepartment = function(roomId=null, departmentId=null, departmentName=null, guestId, callback) {

      if( !roomId || !departmentId || !departmentName ) return false;

      const _userChatRef = this._userRef.child('chats').child(roomId);

      this.leaveRoom(roomId);
      this.setConversationStatus(roomId, guestId, 'closed');
      this.setCurrentRoom(null); // precisa limpar a sala atual
      this._roomRef.child(roomId).update({
        attendentId: null,
        attendentName: null,
        departmentId,
        departmentName,
        waiting: true
      })
      .then(() => {
        return _userChatRef.update({
          departmentId,
          departmentName,
        })
      })
      .then(() => {
        this.sendMessageWidget(roomId, departmentName, "system", "changed_department", ()=>{
            return (typeof callback === "function") && callback();
        });
      })


   }

    Firechat.prototype.previousMessages = function (roomId, oldMessageId) {
      const self = this;
      return self
        ._messageRef
        .child(roomId)
        .orderByKey()
        .limitToLast(self._options.numMaxMessages + 1)
        .endAt(oldMessageId)
        .once('value')
        .then(snapshot => {
          if (snapshot.exists()) {
            return getVal(snapshot);
          }
          return {};
        })
        .then(mapObjects(keyAsId))
        .then(init)
    }

    Firechat.prototype.isChatAssignedToUser = function (contactId, user) {
      return new Promise((resolve, reject) => {
        this.checkIfRoomExists(contactId)
          .then(roomRef => {
            const isChatAssignedToUser = roomRef.attendentId === user.id;
            return isNotNil(roomRef) ? isChatAssignedToUser : false;
          })
          .then(resolve)
          .catch(reject);
      });
    }

    Firechat.getInstance = function(license, options) {
      if(isNil(license)) throw Error('Must have a license to instantiate firechat');
      if(!instance){
        const db = firebase.database().ref(license)
        instance = new Firechat(db, options);
      }
      return instance;
    }

    return Firechat;
  }

  export default _Firechat();



// WEBPACK FOOTER //
// ./src/utils/Firechat.js